import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import {
  Pill, CheckSquare, ShoppingCart, Settings, LayoutGrid, StickyNote,
  Ruler, QrCode, Image as ImageIcon, CalendarDays, Menu, X, Sun, Moon, Heart, Pin, PinOff,
  Inbox, Layers, Receipt, ListChecks, FileText, ScanText, Brain, Utensils,
} from "lucide-react";
import DonateModal from "./DonateModal";
import ConsentBanner from "./ConsentBanner";
import MobileDock from "./MobileDock";
import ShareButton from "./ShareButton";
import AdSlot from "./AdSlot";
import AuthMenu from "./AuthMenu";
import FeatureGate, { isFeatureEnabled } from "./FeatureGate";
import { getTheme, applyTheme, toggleTheme } from "../lib/theme";
import { recordToolUse, getFavorites, togglePin, isPinned } from "../lib/favorites";
import { useDonateStatus } from "../lib/donate";
import { trackPageView } from "../lib/analytics";
import { getConfig } from "../lib/runtimeConfig";
import { API } from "../lib/api";

const TOOLS = [
  { to: "/", label: "Hub", icon: LayoutGrid, testid: "nav-hub" },
  { to: "/inbox", label: "Inbox", icon: Inbox, testid: "nav-inbox", flag: "feature_life_inbox" },
  { to: "/memory", label: "Memory", icon: Brain, testid: "nav-memory", flag: "feature_contextual_memory" },
  { to: "/prescriptions", label: "Prescriptions", icon: Pill, testid: "nav-prescriptions", flag: "show_prescriptions" },
  { to: "/todo", label: "To-Do", icon: CheckSquare, testid: "nav-todo", flag: "show_todo" },
  { to: "/shopping", label: "Shopping", icon: ShoppingCart, testid: "nav-shopping", flag: "show_shopping" },
  { to: "/notes", label: "Notes", icon: StickyNote, testid: "nav-notes", flag: "show_notes" },
  { to: "/routines", label: "Routines", icon: Layers, testid: "nav-routines", flag: "feature_routines" },
  { to: "/life-admin", label: "Life Admin", icon: Receipt, testid: "nav-life-admin", flag: "feature_subscriptions" },
  { to: "/tools/calendar-generator", label: "Calendar Gen", icon: CalendarDays, testid: "nav-cg", flag: "show_calendar_generator" },
  { to: "/tools/unit-converter", label: "Unit Converter", icon: Ruler, testid: "nav-uc", flag: "show_unit_converter" },
  { to: "/tools/image-editor", label: "Image Editor", icon: ImageIcon, testid: "nav-image", flag: "show_image_editor" },
  { to: "/tools/qr-generator", label: "QR Generator", icon: QrCode, testid: "nav-qr", flag: "show_qr_generator" },
  { to: "/tools/text-to-checklist", label: "Text → List", icon: ListChecks, testid: "nav-t2c", flag: "feature_text_to_checklist" },
  { to: "/tools/url-summary", label: "URL Summary", icon: FileText, testid: "nav-us", flag: "feature_url_summary" },
  { to: "/tools/ocr", label: "Image → Text", icon: ScanText, testid: "nav-ocr", flag: "feature_ocr_tool" },
  { to: "/tools/dinner-plate", label: "Dinner Plate", icon: Utensils, testid: "nav-dinner-plate", flag: "feature_dinner_plate" },
];

function toolFor(path) {
  return TOOLS.find(t => t.to === path || (path.startsWith(t.to) && t.to !== "/"));
}

export default function Layout({ children }) {
  const loc = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [theme, setTheme] = useState(getTheme());
  const [showDonate, setShowDonate] = useState(false);
  const [favs, setFavs] = useState([]);
  const [pins, setPins] = useState({});
  const [ctx, setCtx] = useState(null); // {x,y,path}
  const [, setConfigVersion] = useState(0); // Force re-render when config loads
  const donateStatus = useDonateStatus();

  useEffect(() => { applyTheme(theme); }, [theme]);

  // Re-render when runtime config loads (for feature flags)
  useEffect(() => {
    const h = () => setConfigVersion(v => v + 1);
    window.addEventListener("vh-config-loaded", h);
    return () => window.removeEventListener("vh-config-loaded", h);
  }, []);

  function refreshFavs() {
    setFavs(getFavorites(4));
    const map = {};
    for (const t of TOOLS) map[t.to] = isPinned(t.to);
    setPins(map);
  }

  useEffect(() => {
    const tool = toolFor(loc.pathname);
    if (tool && tool.to !== "/" && tool.to !== "/settings") recordToolUse(tool.to);
    refreshFavs();
    setMobileOpen(false);
    trackPageView(loc.pathname + loc.search, document.title);
    const h = () => refreshFavs();
    window.addEventListener("favorites-changed", h);
    return () => window.removeEventListener("favorites-changed", h);
  }, [loc.pathname, loc.search]);

  useEffect(() => {
    if (!ctx) return;
    const close = () => setCtx(null);
    const esc = (e) => { if (e.key === "Escape") setCtx(null); };
    window.addEventListener("click", close);
    window.addEventListener("keydown", esc);
    return () => { window.removeEventListener("click", close); window.removeEventListener("keydown", esc); };
  }, [ctx]);

  function onContextMenu(e, path) {
    e.preventDefault();
    setCtx({ x: e.clientX, y: e.clientY, path });
  }

  function pinFromCtx() {
    if (!ctx) return;
    togglePin(ctx.path);
    setCtx(null);
  }

  const favTools = favs.map(f => TOOLS.find(t => t.to === f)).filter(Boolean).slice(0, 4);

  // Use admin-uploaded logo if present; otherwise the built-in SVG.
  const rtConfig = getConfig();
  const logoSrc = rtConfig.logo_override ? `${API}/admin/assets/logo/current` : "/logo.svg";

  return (
    <div className="min-h-screen relative grain flex" style={{ background: "var(--bg)", color: "var(--text-main)" }}>
      {/* Always-visible desktop rail (240px) */}
      <aside
        data-testid="sidebar"
        className="hidden md:flex fixed left-0 top-0 bottom-0 z-40 flex-col border-r w-60"
        style={{ background: "var(--surface)", borderColor: "var(--border-subtle)" }}
      >
        <div className="h-16 flex items-center px-4 border-b" style={{ borderColor: "var(--border-subtle)" }}>
          <Link to="/" className="flex items-center gap-2 min-w-0" data-testid="logo-link">
            <img src={logoSrc} alt="VeriHandi" className="h-9 w-9 shrink-0" />
            <div className="min-w-0">
              <div className="font-display font-black tracking-tighter text-lg leading-none">VeriHandi</div>
              <div className="text-[10px] uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>verihandi.com</div>
            </div>
          </Link>
        </div>
        <nav className="flex-1 overflow-y-auto py-4 px-2 space-y-1" aria-label="Primary">
          {TOOLS.filter(t => !t.flag || isFeatureEnabled(t.flag)).map(({ to, label, icon: Icon, testid }) => (
            <div key={to} onContextMenu={(e) => onContextMenu(e, to)} className="relative">
              <NavLink
                to={to}
                end={to === "/"}
                data-testid={testid}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    isActive ? "text-[color:var(--primary-ink)]" : "hover:bg-[color:var(--surface-2)]"
                  }`
                }
                style={({ isActive }) => isActive ? { background: "var(--primary-soft)" } : {}}
                title="Right-click to pin"
              >
                <Icon className="h-[18px] w-[18px] shrink-0" />
                <span className="truncate flex-1">{label}</span>
                {pins[to] && <Pin className="h-3 w-3 shrink-0" style={{ color: "var(--accent)" }} data-testid={`pin-indicator-${to}`} />}
              </NavLink>
            </div>
          ))}
        </nav>
        <div className="border-t p-2" style={{ borderColor: "var(--border-subtle)" }}>
          <NavLink to="/settings" data-testid="nav-settings"
            className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium ${isActive ? "text-[color:var(--primary-ink)]" : "hover:bg-[color:var(--surface-2)]"}`}
            style={({ isActive }) => isActive ? { background: "var(--primary-soft)" } : {}}
          >
            <Settings className="h-[18px] w-[18px] shrink-0" />
            <span>Settings</span>
          </NavLink>
        </div>
      </aside>

      {/* Context menu */}
      {ctx && (
        <div
          data-testid="sidebar-context-menu"
          className="fixed z-50 rounded-lg border shadow-lg py-1 min-w-[200px]"
          style={{ top: ctx.y, left: ctx.x, background: "var(--surface)", borderColor: "var(--border-subtle)" }}
          onClick={(e) => e.stopPropagation()}
        >
          <button onClick={pinFromCtx} data-testid="ctx-toggle-pin" className="w-full text-left px-3 py-2 text-sm hover:bg-[color:var(--surface-2)] flex items-center gap-2">
            {isPinned(ctx.path) ? <><PinOff className="h-4 w-4" /> Unpin from favorites</> : <><Pin className="h-4 w-4" /> Pin to favorites</>}
          </button>
        </div>
      )}

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <aside className="absolute left-0 top-0 bottom-0 w-72 flex flex-col" style={{ background: "var(--surface)" }} data-testid="sidebar-mobile">
            <div className="h-16 flex items-center justify-between px-4 border-b" style={{ borderColor: "var(--border-subtle)" }}>
              <Link to="/" className="flex items-center gap-2">
                <img src={logoSrc} alt="" className="h-9 w-9 shrink-0" />
                <span className="font-display font-black tracking-tighter text-lg">VeriHandi</span>
              </Link>
              <button onClick={() => setMobileOpen(false)} className="p-2 rounded-lg hover:bg-[color:var(--surface-2)]" data-testid="close-mobile-nav"><X className="h-5 w-5" /></button>
            </div>
            <nav className="flex-1 overflow-y-auto py-4 px-2 space-y-1">
              {TOOLS.filter(t => !t.flag || isFeatureEnabled(t.flag)).map(({ to, label, icon: Icon, testid }) => (
                <NavLink key={to} to={to} end={to === "/"} data-testid={`${testid}-m`}
                  className={({ isActive }) => `flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium ${isActive ? "text-[color:var(--primary-ink)]" : "hover:bg-[color:var(--surface-2)]"}`}
                  style={({ isActive }) => isActive ? { background: "var(--primary-soft)" } : {}}
                  onContextMenu={(e) => onContextMenu(e, to)}
                >
                  <Icon className="h-5 w-5" /> <span className="flex-1">{label}</span>
                  {pins[to] && <Pin className="h-3 w-3" style={{ color: "var(--accent)" }} />}
                </NavLink>
              ))}
              <NavLink to="/settings" className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium hover:bg-[color:var(--surface-2)]">
                <Settings className="h-5 w-5" /> Settings
              </NavLink>
            </nav>
          </aside>
        </div>
      )}

      {/* Main column */}
      <div className="flex-1 flex flex-col min-w-0 md:pl-60">
        {/* Top nav */}
        <header
          className="sticky top-0 z-30 h-16 flex items-center justify-between px-4 sm:px-6 lg:px-8 border-b backdrop-blur no-print"
          style={{ background: "color-mix(in srgb, var(--surface) 86%, transparent)", borderColor: "var(--border-subtle)" }}
        >
          <div className="flex items-center gap-3 min-w-0">
            <button onClick={() => setMobileOpen(true)} className="md:hidden p-2 -ml-2 rounded-lg hover:bg-[color:var(--surface-2)]" data-testid="open-mobile-nav" aria-label="Open menu">
              <Menu className="h-5 w-5" />
            </button>
            <Link to="/" className="md:hidden flex items-center gap-2 min-w-0" data-testid="mobile-brand">
              <img src={logoSrc} alt="" className="h-8 w-8 shrink-0" />
              <span className="font-display font-black tracking-tighter text-lg truncate">VeriHandi</span>
            </Link>
            <span className="hidden md:inline label-caps truncate" style={{ color: "var(--text-muted)" }}>Very handy tools for everyday tasks</span>
          </div>
          <div className="flex items-center gap-1 sm:gap-2">
            {donateStatus.configured && (
              <button onClick={() => setShowDonate(true)} data-testid="donate-button"
                className="inline-flex items-center gap-2 px-3 sm:px-4 h-9 rounded-lg text-sm font-semibold transition-colors"
                style={{ background: "var(--accent)", color: "white" }} title="Support VeriHandi"
              >
                <Heart className="h-4 w-4" /> <span className="hidden sm:inline">Donate</span>
              </button>
            )}
            <ShareButton />
            <AuthMenu />
            <button onClick={() => setTheme(toggleTheme())} data-testid="theme-toggle"
              className="h-9 w-9 rounded-lg flex items-center justify-center hover:bg-[color:var(--surface-2)]"
              aria-label="Toggle theme" title={theme === "dark" ? "Switch to light" : "Switch to dark"}
            >
              {theme === "dark" ? <Sun className="h-[18px] w-[18px]" /> : <Moon className="h-[18px] w-[18px]" />}
            </button>
          </div>
        </header>

        {/* Content + right rail (desktop) */}
        <div className="relative z-10 w-full max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12 pb-28 md:pb-12 flex gap-8">
          <main className="flex-1 min-w-0">
            {children}
          </main>
          <aside className="hidden xl:block w-[300px] shrink-0">
            <div className="sticky top-24">
              <AdSlot format="sidebar" testid="ad-sidebar" />
              <div className="mt-6 card-tactile rounded-xl p-4 text-xs" style={{ color: "var(--text-muted)" }}>
                <div className="label-caps mb-2">Why VeriHandi</div>
                <ul className="space-y-1.5">
                  <li>· No accounts, no tracking</li>
                  <li>· All data stays on your device</li>
                  <li>· AI that works for you</li>
                  <li>· Lightweight and fast</li>
                </ul>
              </div>
            </div>
          </aside>
        </div>

        <footer className="relative z-10 max-w-[1200px] mx-auto w-full px-4 sm:px-6 lg:px-8 py-8 text-sm border-t no-print" style={{ color: "var(--text-muted)", borderColor: "var(--border-subtle)" }}>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <span>VeriHandi — very handy tools for everyday tasks · <a href="https://verihandi.com" className="hover:underline">verihandi.com</a></span>
            <span className="flex items-center gap-4">
              <Link to="/privacy" className="hover:underline" data-testid="footer-privacy">Privacy</Link>
              <span className="label-caps">v1.20</span>
            </span>
          </div>
        </footer>
      </div>

      {/* Mobile bottom dock (replaces old floating favourites pill) */}
      <MobileDock favTools={favTools} pins={pins} />

      <DonateModal open={showDonate} onClose={() => setShowDonate(false)} />
      <ConsentBanner />
    </div>
  );
}
